'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Shuffle, Repeat, ListMusic, ChevronUp, ChevronDown, Heart, AlertCircle,
} from 'lucide-react';

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function GlobalMusicPlayerBar() {
  const {
    playlist, currentIndex, track, isPlaying, progress, duration, currentTime,
    volume, muted, shuffle, repeat, expanded, liked, audioError, loadingTracks,
    setVolume, setMuted, setShuffle, setRepeat, setExpanded, setAudioError,
    handlePlayPause, handleNext, handlePrev, handleSeek, toggleLike, playTrackAt,
  } = useMusicPlayer();

  // Don't render player if no tracks loaded and not loading
  if (!loadingTracks && playlist.length === 0) {
    return null;
  }

  const onSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    handleSeek(pct);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-t border-border">
      {/* Audio error banner */}
      {audioError && (
        <div className="flex items-center gap-2 px-6 py-2 bg-red-950/80 border-b border-red-800/40 text-red-300 text-xs">
          <AlertCircle size={13} className="shrink-0" />
          <span>{audioError}</span>
          <button onClick={() => setAudioError(null)} className="ml-auto text-red-400 hover:text-red-200">✕</button>
        </div>
      )}

      {/* Expanded playlist */}
      {expanded && (
        <div className="border-b border-border bg-background/95 max-h-64 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Queue ({playlist.length})</p>
            </div>
            <div className="space-y-1">
              {playlist.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => playTrackAt(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                    i === currentIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted/40 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <div className="relative w-9 h-9 rounded-md overflow-hidden shrink-0">
                    <AppImage src={t.cover} alt={t.coverAlt} fill className="object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-500 truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.artist}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!t.audioUrl && <span className="text-[10px] text-yellow-500/70">No audio</span>}
                    <span className="text-xs font-mono text-muted-foreground">{t.duration}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main player bar */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 h-20 flex items-center gap-3 sm:gap-6">
        {/* Track info */}
        <div className="flex items-center gap-3 w-40 sm:w-64 shrink-0">
          <div className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-muted/30">
            {track?.cover ? (
              <AppImage src={track.cover} alt={track.coverAlt || track.title} fill className="object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ListMusic size={18} className="text-muted-foreground/40" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 hidden sm:block">
            <p className="text-sm font-600 text-foreground truncate">{track?.title ?? (loadingTracks ? 'Loading...' : 'No tracks')}</p>
            <p className="text-xs text-muted-foreground truncate">{track?.artist ?? ''}</p>
          </div>
          {track && (
            <button
              onClick={() => track && toggleLike(track.id)}
              className={`p-1.5 rounded-lg transition-all hidden sm:inline-flex ${liked.includes(track?.id ?? '') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Heart size={16} fill={liked.includes(track?.id ?? '') ? 'currentColor' : 'none'} />
            </button>
          )}
        </div>

        {/* Controls + progress */}
        <div className="flex-1 flex flex-col items-center gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => setShuffle(!shuffle)}
              className={`p-1.5 rounded transition-all hidden sm:inline-flex ${shuffle ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Shuffle size={15} />
            </button>
            <button
              onClick={handlePrev}
              disabled={playlist.length === 0}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-all disabled:opacity-30"
            >
              <SkipBack size={18} />
            </button>
            <button
              onClick={handlePlayPause}
              disabled={playlist.length === 0}
              className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 active:scale-95 transition-all disabled:opacity-30"
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button
              onClick={handleNext}
              disabled={playlist.length === 0}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-all disabled:opacity-30"
            >
              <SkipForward size={18} />
            </button>
            <button
              onClick={() => setRepeat(!repeat)}
              className={`p-1.5 rounded transition-all hidden sm:inline-flex ${repeat ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Repeat size={15} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="w-full flex items-center gap-3 max-w-xl">
            <span className="text-[10px] font-mono text-muted-foreground w-8 text-right shrink-0">{formatTime(currentTime)}</span>
            <div
              className="flex-1 h-1 bg-muted/40 rounded-full cursor-pointer group relative"
              onClick={onSeekClick}
            >
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity -translate-x-1/2"
                style={{ left: `${progress}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-muted-foreground w-8 shrink-0">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume + queue */}
        <div className="flex items-center gap-2 sm:gap-3 w-auto sm:w-48 shrink-0 justify-end">
          <button
            onClick={() => setMuted(!muted)}
            className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-all hidden sm:inline-flex"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            className="w-20 accent-primary hidden sm:block"
          />
          <button
            onClick={() => setExpanded(!expanded)}
            className={`p-1.5 rounded transition-all ${expanded ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
